import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from '@betty/toolkit/state';
import {
  selectRegistrationConfig,
  selectRegistrationFormPromoId,
  selectRegistrationOffer,
  selectRegistrationProgress,
} from '../../state';
import { Box, ProgressBar, Text, Timer, Image } from '@betty/moonlight';
import RegistrationOfferImage from '../RegistrationOfferImage/RegistrationOfferImage';
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

function RegistrationProgressHeader() {
  const config = useSelector(selectRegistrationConfig);
  const progress = useSelector(selectRegistrationProgress);
  const offerId = useSelector(selectRegistrationFormPromoId) || -1;
  const offer = useSelector(selectRegistrationOffer(offerId));
  const rotation = useSharedValue(0);
  const rewards = useMemo(() => offer?.rewards || [], [offer?.rewards]);

  useEffect(() => {
    rotation.value = withRepeat(
      withTiming(-20, {
        duration: 500,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true,
    );
  }, [rotation]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${rotation.value}deg`,
        },
      ],
    };
  });

  const timer = useMemo(() => {
    if (config.countdownTimer) {
      return Math.floor(Date.now() / 1000) + config.countdownTimer;
    }
    return null;
  }, [config.countdownTimer]);

  if (!offer) {
    return null;
  }

  return (
    <Box alignItems="center" width="100%" marginTop="space-300">
      <Box
        width={{
          mobile: '100%',
          tablet: '100%',
          desktop: 760,
        }}
      >
        <Box flexDirection="row" alignItems="center" gap="space-300">
          <Box
            justifyContent="center"
            alignItems="center"
            paddingHorizontal="space-400"
          >
            <RegistrationOfferImage
              width={80}
              height={80}
              rewards={rewards}
              miniImageUrl={offer?.miniImageUrl || ''}
            />
          </Box>
          <Box flex={1} justifyContent="center">
            <Box>
              {timer ? (
                <Box flexDirection="row" alignItems="center" gap="space-100">
                  <Box width={25} height={25} animatedStyle={animatedStyle}>
                    <Image
                      contentFit="contain"
                      width="100%"
                      height="100%"
                      name="common.Clock"
                      priority="high"
                      cachePolicy="memory-disk"
                      transform={[{ rotate: '10deg' }]}
                    />
                  </Box>
                  <Timer
                    color="darkSurfacePrimary"
                    variant="body-s-semi"
                    time={timer}
                  />
                  <Text
                    variant="body-s"
                    locNS="registration"
                    locKey="claimBonus"
                    color="darkSurfaceTertiary"
                  ></Text>
                </Box>
              ) : (
                <Text
                  variant="body-s-semi"
                  locNS="registration"
                  locKey="yourBonus"
                  color="darkSurfaceTertiary"
                ></Text>
              )}
            </Box>
            <Box flexDirection="row" alignItems="center">
              <Text variant="h5" color="level_2">{`${progress}% `}</Text>
              <Text
                variant="body-s"
                locNS="registration"
                locKey="completed"
                color="darkSurfaceTertiary"
              ></Text>
            </Box>
            <Box maxWidth="95%">
              <ProgressBar
                from={config.startPercentage || 0}
                to={config.endPercentage || 100}
                progress={progress}
                backgroundColor="mutedBackground"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
export default memo(RegistrationProgressHeader);
